import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import AddFinanceRecordModal from '../Modal/AddFinanceRecordModal';
import RecordPayoutModal from '../Modal/RecordPayoutModal';
import Pagination from '../Pagination/Pagination';
import AutoparkOperations from './AutoparkOperations';
import { FinanceService, IFinanceRecord, IFinanceFilters } from '../../services/finance.service';
import './adminFinances.css';
import { IAutopark } from '../../services/autopark.service';
import { Money, formatMoney } from '../../utils/money';
import { DatePeriod, NO_DATE, formatBackendDate, getMoscowPeriodRange } from '../../utils/date.utils';
import { getPayoutAttempt } from '../../utils/payoutAttempts';

function AdminFinances({ autopark }: { autopark: IAutopark }) {
  const { autoparkId } = useParams();
  const [financeRecords, setFinanceRecords] = useState<IFinanceRecord[]>([]);
  // GET /autoparks отдаёт баланс числом; точное значение приходит из выплаты и истории операций
  const [balance, setBalance] = useState<Money>(autopark.balance);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(20);
  const [filterType, setFilterType] = useState('default');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [payoutRecord, setPayoutRecord] = useState<IFinanceRecord | null>(null);
  const [operationsRefreshKey, setOperationsRefreshKey] = useState(0);
  const [editingRecordId, setEditingRecordId] = useState<string | null>(null);
  const [editingNotes, setEditingNotes] = useState('');

  const dateRange = filterType !== 'default' ? getMoscowPeriodRange(filterType as DatePeriod) : undefined;

  useEffect(() => {
    setBalance(autopark.balance);
  }, [autopark.id, autopark.balance]);

  // Функция загрузки данных
  // Номер последнего запроса: ответы на устаревшие запросы игнорируем
  const recordsRequestRef = useRef(0);

  const loadFinanceRecords = async (page: number = currentPage) => {
    const requestId = ++recordsRequestRef.current;
    setLoading(true);
    setError(null);

    try {
      const filters: IFinanceFilters = {
        page,
        pageSize,
        autoparkID: autopark.id,
        // Фильтр по дате заявки
        dateFrom: dateRange?.dateFrom,
        dateTo: dateRange?.dateTo,
      };

      const response = await FinanceService.getBalances(filters);
      if (requestId !== recordsRequestRef.current) return;

      setFinanceRecords(response.data);
      setCurrentPage(response.pagination.page);
      setTotalPages(response.pagination.totalPages);
    } catch (err: any) {
      if (requestId === recordsRequestRef.current) setError(err.message);
    } finally {
      if (requestId === recordsRequestRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    loadFinanceRecords(1);
  }, [autoparkId, filterType, pageSize]);

  const getStatusText = (status: string) => {
    switch (status) {
      case 'paid':
        return 'Выплачено';
      case 'notPaid':
        return 'Не выплачено';
      case 'partiallyPaid':
        return 'Частично выплачено';
      default:
        return status;
    }
  };

  const handleAddRecord = () => {
    setIsAddModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsAddModalOpen(false);
  };

  const handleConfirmAddRecord = () => {
    // После добавления записи перезагружаем данные
    loadFinanceRecords(1);
  };

  const handleEditRecord = (record: IFinanceRecord) => {
    setEditingRecordId(record.id);
    setEditingNotes(record.notes ?? '');
  };

  const handleSaveRecord = async () => {
    if (!editingRecordId) return;

    try {
      setLoading(true);
      // Выплаченную сумму напрямую не меняем — только через выплату
      const updated = await FinanceService.updateBalance(editingRecordId, { notes: editingNotes });

      setFinanceRecords((prevRecords) => prevRecords.map((r) => (r.id === updated.id ? updated : r)));
      setEditingRecordId(null);
      setEditingNotes('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingRecordId(null);
    setEditingNotes('');
  };

  // Повтор выплаты возвращает запись и баланс на момент первого вызова, поэтому
  // перезагружаем записи и историю (её конверт обновит баланс), а не берём их из ответа
  const handlePayoutRecorded = () => {
    loadFinanceRecords(currentPage);
    setOperationsRefreshKey((key) => key + 1);
  };

  const handlePayoutAlreadyRecorded = () => {
    loadFinanceRecords(currentPage);
    setOperationsRefreshKey((key) => key + 1);
  };

  // Кнопка выплаты нужна, пока есть остаток или незавершённая попытка
  const canRecordPayout = (record: IFinanceRecord) => record.status !== 'paid' || getPayoutAttempt(record.id) !== null;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadFinanceRecords(page);
  };

  const formatCurrency = (amount: Money) => {
    return formatMoney(amount) + ' Р';
  };

  return (
    <div className="admin-finances">
      {/* Баланс автопарка */}
      <div className="balance-card">
        <div className="balance-card__icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
            <circle cx="23.9993" cy="16" r="1.33333" fill="white" />
            <path
              d="M29.3333 14C29.3333 13.8968 29.3333 13.2897 29.3303 13.2461C29.2831 12.5785 28.7104 12.0466 27.9913 12.0029C27.9444 12 27.8889 12 27.7778 12H24.3077C21.9286 12 20 13.7909 20 16C20 18.2091 21.9286 20 24.3077 20H27.7778C27.8889 20 27.9444 20 27.9913 19.9971C28.7104 19.9534 29.2831 19.4215 29.3303 18.7539C29.3333 18.7103 29.3303 18.1032 29.3303 18"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M17.3327 5.33337C22.361 5.33337 24.8752 5.33337 26.4372 6.89547C27.5148 7.97304 27.8491 9.50364 27.9527 12M13.3327 26.6667H17.3327C22.361 26.6667 24.8752 26.6667 26.4373 25.1046C27.5148 24.027 27.8491 22.4964 27.9527 20.0001M11.9993 5.33461C7.84694 5.34677 5.64557 5.47801 4.22811 6.89547C2.66602 8.45757 2.66602 10.9717 2.66602 16C2.66602 21.0284 2.66602 23.5425 4.22811 25.1046C5.09902 25.9755 6.26586 26.3609 7.99935 26.5314"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M9.33398 12L9.33398 13.3333M9.33398 20L9.33398 16.6667"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
        </div>
        <div className="balance-card__content">
          <span className="balance-card__title">Баланс автопарка "{autopark.tradeName}"</span>
          <span className="balance-card__amount">
            {formatMoney(balance)} <span className="balance-card__amount-currency">Р</span>
          </span>
        </div>
      </div>

      {/* Контролы и фильтры */}
      <div className="controls-section">
        <div className="filters">
          <div className="filter-item">
            <label>Фильтр по датам</label>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option value="default">По умолчанию</option>
              <option value="week">За неделю</option>
              <option value="month">За месяц</option>
              <option value="year">За год</option>
            </select>
          </div>
        </div>
        <button className="add-record-btn" onClick={handleAddRecord}>
          <span>Добавить запись</span>
          <div className="add-record-btn__icon">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 1V15M1 8H15" stroke="white" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
        </button>
      </div>

      <div className="content-section">
        {/* Таблица финансов */}
        <div className="finance-table-section">
          {loading ? (
            <div className="loading-state">Загрузка финансовых записей...</div>
          ) : error ? (
            <div className="error-state">{error}</div>
          ) : financeRecords.length === 0 ? (
            <div className="empty-state">Финансовые записи не найдены</div>
          ) : (
            <div className="finance-table">
              {financeRecords.map((record) => (
                <div
                  key={record.id}
                  className={`finance-table-row ${editingRecordId === record.id ? 'finance-table-row--editing' : ''}`}
                >
                  <div className="finance-table-row__block finance-table-row__block--date">
                    <div className="finance-table-row__label">Дата выплаты:</div>
                    <div className="finance-table-row__value">{formatBackendDate(record.dateOfPaid, NO_DATE)}</div>
                  </div>

                  <div className="finance-table-row__block finance-table-row__block--amount">
                    <div className="finance-table-row__label">Сумма выплаты:</div>
                    <div className="finance-table-row__value">{formatCurrency(record.price)}</div>
                  </div>

                  <div className="finance-table-row__block finance-table-row__block--paid">
                    <div className="finance-table-row__label">Фактически выплачено:</div>
                    <div className="finance-table-row__value">{formatCurrency(record.paid)}</div>
                  </div>

                  <div className="finance-table-row__block finance-table-row__block--note">
                    <div className="finance-table-row__label">Примечание:</div>
                    <div className="finance-table-row__value">
                      {editingRecordId === record.id ? (
                        <textarea
                          value={editingNotes}
                          onChange={(e) => setEditingNotes(e.target.value)}
                          className="edit-textarea"
                          rows={2}
                        />
                      ) : (
                        record.notes
                      )}
                    </div>
                  </div>

                  <div className="finance-table-row__status">
                    <div className={`status-badge status-badge--${record.status.toLowerCase()}`}>
                      {getStatusText(record.status)}
                    </div>
                  </div>

                  <div className="finance-table-row__action">
                    {editingRecordId === record.id ? (
                      <div className="edit-actions">
                        <button className="admin-finances-save-btn" onClick={handleSaveRecord} title="Сохранить">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path
                              d="M13.5 4.5L6 12L2.5 8.5"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                        <button className="admin-finances-cancel-btn" onClick={handleCancelEdit} title="Отмена">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path
                              d="M12 4L4 12M4 4L12 12"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <>
                        <button className="edit-btn" onClick={() => handleEditRecord(record)}>
                          Изменить
                        </button>
                        {canRecordPayout(record) && (
                          <button className="edit-btn" onClick={() => setPayoutRecord(record)}>
                            Выплатить
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Пагинация */}
          {totalPages > 1 && (
            <div className="finance-pagination">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
            </div>
          )}
        </div>
      </div>

      <AutoparkOperations
        autoparkId={autopark.id}
        dateFrom={dateRange?.dateFrom}
        dateTo={dateRange?.dateTo}
        refreshKey={operationsRefreshKey}
        onBalanceLoaded={setBalance}
      />

      {/* Модальное окно для добавления записи */}
      <AddFinanceRecordModal
        isOpen={isAddModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmAddRecord}
        autoparkId={autopark.id}
        autoparkBalance={balance}
      />

      {/* Модальное окно для записи выплаты */}
      <RecordPayoutModal
        isOpen={payoutRecord !== null}
        record={payoutRecord}
        autoparkBalance={balance}
        onClose={() => setPayoutRecord(null)}
        onRecorded={handlePayoutRecorded}
        onAlreadyRecorded={handlePayoutAlreadyRecorded}
      />
    </div>
  );
}

export default AdminFinances;
